import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateHourDb } from "../utils/tools";
import { customDateFormatType } from "../utils/utils";
import { TypeTransferRequests } from "../utils/contants";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.transferRequests;
const queryKey = "transferRequests";

async function fetchTransferRequests(
  currentPage,
  search,
  type,
  oldEmployeeId,
  newEmployeeId,
  status,
  startDate,
  endDate
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (type) options.type = type;
    if (status) options.status = status;

    if (oldEmployeeId) options.oldEmployeeId = oldEmployeeId;
    if (newEmployeeId) options.newEmployeeId = newEmployeeId;
    if (startDate) options.startTime = customDateFormatType(startDate);
    if (endDate) options.endTime = customDateFormatType(endDate);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/transferRequests?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      let type = "-";
      let apartment = "-";
      let customer = "-";
      let project = "-";
      if (item?.type === TypeTransferRequests.BOOKING) {
        type = "Booking";
        apartment = item?.booking?.apartmentObject?.code || "-";
        customer = item?.booking?.customerObject?.name || "-";
        project = item?.booking?.projectObject?.name || "-";
      } else if (item?.type === TypeTransferRequests.LOCKING) {
        type = "Locking";
        apartment = item?.locking?.apartmentObject?.code || "-";
        customer = item?.locking?.customerObject?.name || "-";
        project = item?.locking?.projectObject?.name || "-";
      } else if (item?.type === TypeTransferRequests.TTKQ) {
        type = "Ttkq";
        apartment = item?.ttkq?.apartmentObject?.code || "-";
        customer = item?.ttkq?.customerObject?.name || "-";
        project = item?.ttkq?.projectObject?.name || "-";
      } else if (item?.type === TypeTransferRequests.SALESCONTRACT) {
        type = "HĐMB";
        apartment = item?.salesContract?.apartmentObject?.code || "-";
        customer = item?.salesContract?.customerObject?.name || "-";
        project = item?.salesContract?.projectObject?.name || "-";
      }

      return {
        id: item?._id || "",
        status: item?.status || "-",
        type: type || "",
        oldEmployee: item?.oldEmployeeObject?.fullName || "-",
        newEmployee: item?.newEmployeeObject?.fullName || "-",
        project: project || "-",
        apartment: apartment || "-",
        customer: customer || "-",
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useTransferRequests(
  search,
  type,
  oldEmployeeId,
  newEmployeeId,
  status,
  startDate,
  endDate
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      type,
      oldEmployeeId,
      newEmployeeId,
      status,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchTransferRequests(
        currentPage,
        search,
        type,
        oldEmployeeId,
        newEmployeeId,
        status,
        startDate,
        endDate
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    transferRequestsData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
